// src/components/PcShowcase.js
// A desktop-PC showcase: monitor + stand + keyboard + mouse. The monitor
// screen runs an auto-advancing slideshow of an offering's folder photos.
//
// Images are auto-probed from /public following a `<base><n>.jpeg` pattern
// (e.g. urus.jpeg, urus1..7.jpeg), so adding photos to the folder "just works".
import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const MAX_PROBE = 20;
const AUTO_MS = 3000;

/**
 * @param folder  public path to the folder, e.g. "/01pengurusan_automasi"
 * @param base    filename base, e.g. "urus" -> urus.jpeg, urus1.jpeg, ...
 * @param delay   stagger the slideshow start so the 3 PCs don't sync
 * @param screens optional screenshot files in the same folder. First one shows
 *                first (the login page, un-blurred); the rest are lightly blurred
 *                and interleaved between the real photos.
 */
const PcShowcase = ({ folder, base, alt = 'Paparan', delay = 0, screens = [] }) => {
  // each item: { src, blur }
  const [imgs, setImgs] = useState([]);
  const [i, setI] = useState(0);
  const timer = useRef(null);

  useEffect(() => {
    let cancelled = false;
    const EXTS = ['jpeg', 'jpg', 'png', 'webp'];

    // Try one filename stem across all extensions; resolve the first that loads.
    const probeStem = (stem) =>
      new Promise((resolve) => {
        let idx = 0;
        const tryNext = () => {
          if (idx >= EXTS.length) return resolve(null);
          const src = `${folder}/${stem}.${EXTS[idx++]}`;
          const im = new Image();
          im.onload = () => resolve(src);
          im.onerror = tryNext;
          im.src = src;
        };
        tryNext();
      });

    // stems: base, base1, base2 .. baseN (preserves numeric order)
    const stems = [base, ...Array.from({ length: MAX_PROBE }, (_, n) => `${base}${n + 1}`)];

    Promise.all(stems.map(probeStem)).then((res) => {
      if (cancelled) return;
      const photos = res.filter(Boolean).map((src) => ({ src, blur: false }));
      // login screenshot first (crisp); remaining screenshots blurred a touch.
      // encodeURI handles spaces / &  / () in the screenshot filenames.
      const [login, ...rest] = screens.map((s) => encodeURI(`${folder}/${s}`));
      const restShots = rest.map((src) => ({ src, blur: true }));

      // sequence: login → photo → shot → photo → shot → ... then leftovers
      const seq = [];
      if (login) seq.push({ src: login, blur: false });
      const maxLen = Math.max(photos.length, restShots.length);
      for (let n = 0; n < maxLen; n++) {
        if (photos[n]) seq.push(photos[n]);
        if (restShots[n]) seq.push(restShots[n]);
      }
      setImgs(seq.length ? seq : photos);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folder, base]);

  useEffect(() => {
    if (imgs.length <= 1) return;
    const start = setTimeout(() => {
      timer.current = setInterval(() => setI((p) => (p + 1) % imgs.length), AUTO_MS);
    }, delay);
    return () => {
      clearTimeout(start);
      clearInterval(timer.current);
    };
  }, [imgs.length, delay]);

  return (
    <div className="w-full select-none">
      {/* ===== Monitor ===== */}
      <div className="mx-auto max-w-[420px]">
        <div className="rounded-2xl border-[3px] border-[var(--z-ink)] bg-[var(--z-ink)] p-2.5 shadow-[6px_8px_0_0_var(--z-accent)]">
          {/* screen */}
          <div className="relative overflow-hidden rounded-lg border-2 border-[var(--z-ink)] bg-black aspect-[16/10]">
            {/* subtle screen glare */}
            <div className="pointer-events-none absolute inset-0 z-10 rounded-lg shadow-[inset_0_0_50px_rgba(0,0,0,0.5)]" />
            <div className="pointer-events-none absolute -left-1/3 top-0 z-10 h-full w-1/3 -skew-x-12 bg-white/10" />
            {imgs.length > 0 ? (
              <AnimatePresence mode="wait">
                <motion.img
                  key={imgs[i].src}
                  src={imgs[i].src}
                  alt={`${alt} ${i + 1}`}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5 }}
                  className="absolute inset-0 h-full w-full object-cover"
                  style={imgs[i].blur ? { filter: 'blur(2.5px)' } : undefined}
                />
              </AnimatePresence>
            ) : (
              <div className="absolute inset-0 grid place-items-center">
                <span className="font-mono text-xs text-white/40">Memuatkan…</span>
              </div>
            )}
          </div>
        </div>
        {/* stand */}
        <div className="mx-auto h-5 w-16 rounded-b-md bg-[var(--z-ink)]" />
        <div className="mx-auto h-2.5 w-40 rounded-full bg-[var(--z-ink)]" />
      </div>

      {/* ===== Keyboard + mouse ===== */}
      <div className="mx-auto mt-3 flex max-w-[420px] items-end gap-3 px-2">
        {/* keyboard */}
        <div className="flex-1 rounded-lg border-2 border-[var(--z-ink)] bg-white p-2 shadow-[3px_3px_0_0_var(--z-ink)]">
          <div className="grid grid-cols-10 gap-1">
            {Array.from({ length: 30 }).map((_, k) => (
              <div key={k} className="h-1.5 rounded-[2px] bg-[var(--z-line)]" />
            ))}
            {/* spacebar */}
            <div className="col-span-10 mt-1 h-1.5 rounded-[2px] bg-[var(--z-line)]" />
          </div>
        </div>
        {/* mouse */}
        <div className="h-8 w-5 rounded-full border-2 border-[var(--z-ink)] bg-white shadow-[2px_2px_0_0_var(--z-ink)]">
          <div className="mx-auto mt-1 h-2 w-[2px] rounded bg-[var(--z-line)]" />
        </div>
      </div>

      {/* dot indicators */}
      {imgs.length > 1 && (
        <div className="mt-3 flex items-center justify-center gap-1.5">
          {imgs.map((_, d) => (
            <button
              key={d}
              type="button"
              onClick={() => setI(d)}
              aria-label={`Gambar ${d + 1}`}
              className={`h-2 rounded-full transition-all ${
                d === i ? 'w-5 bg-[var(--z-accent)]' : 'w-2 bg-[var(--z-line)] hover:bg-[var(--z-accent)]/50'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default PcShowcase;
